import { useCallback } from 'react';
import { clone } from 'lodash-es';
import { v4 as uuid } from 'uuid';
import styled from '@emotion/styled';

import { Text, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import type { ContextOfUse, DateRange, FilterType } from '@hotjar/global-filters';
import type { PlanComparisonReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { StepSeries } from '@hotjar/dashboard-quantz';
import { getStepSeriesLabel } from '@hotjar/dashboard-quantz';
import { Steps } from '../../TrendsPage/Steps';
import { FunnelStepWrapper } from './FunnelStepWrapper';
import { AddStepSection } from '../../TrendsPage/AddStepSection';

const MAX_NB_OF_STEPS = 4;
const MIN_NB_OF_STEPS = 2;

const StepsHeader = styled(Text)`
  margin-bottom: ${tokens.spacer.x2};
`;

interface FunnelStepsProps {
  stepsWithError: string[];
  stepSeriesList: StepSeries[];
  dateRange?: DateRange;
  showReducedUi: boolean;
  onStepSeriesListUpdate: (stepSeriesList: StepSeries[], dateRange?: DateRange) => void;
  onUpsellRequest: (
    featureName: PlanComparisonReferrer,
    additionalTrackingData?: {
      // eslint-disable-next-line camelcase
      ui_position: string;
    },
  ) => void;
  trackClick: (
    eventName: string,
    additionalTrackingData: {
      // eslint-disable-next-line camelcase
      ui_position: string;
    },
  ) => void;
  contextOfUse: ContextOfUse;
}

export const FunnelSteps = ({
  stepsWithError,
  stepSeriesList,
  dateRange,
  showReducedUi,
  onStepSeriesListUpdate,
  onUpsellRequest,
  trackClick,
  contextOfUse,
}: FunnelStepsProps): JSX.Element => {
  const { t } = useTranslation('funnels');

  const updateOne = useCallback(
    (key: string, stepInUiFormat: FilterType[], newDateRange?: DateRange) => {
      const cloned = clone(stepSeriesList);
      const index = cloned.findIndex((item) => item.key === key);
      if (index !== null) {
        cloned[index] = {
          ...cloned[index],
          stepInUiFormat,
          label: getStepSeriesLabel(stepInUiFormat),
        };
        onStepSeriesListUpdate(cloned, newDateRange);
      }
    },
    [stepSeriesList, onStepSeriesListUpdate],
  );

  const addOne = useCallback(() => {
    if (stepSeriesList.length >= MAX_NB_OF_STEPS) {
      return;
    }
    const newChain: StepSeries = {
      key: uuid(),
      stepInUiFormat: [],
      label: getStepSeriesLabel([]),
    };
    onStepSeriesListUpdate([...stepSeriesList, newChain]);
  }, [stepSeriesList, onStepSeriesListUpdate]);

  const removeOne = useCallback(
    (key: string) => {
      if (stepSeriesList.length <= MIN_NB_OF_STEPS) {
        return;
      }
      const filtered = stepSeriesList.filter((item) => item.key !== key);
      onStepSeriesListUpdate(filtered);
    },
    [stepSeriesList, onStepSeriesListUpdate],
  );

  return (
    <>
      <StepsHeader as="p" textType="subtitle2">
        {t`sessionsWhereUsersPerformed`}
      </StepsHeader>
      <Steps>
        {stepSeriesList.map((item, index) => {
          const isLastStepSeries = index === stepSeriesList.length - 1;
          const isFiltersDropdownVisibleByDefault =
            index > 1 && index === stepSeriesList.length - 1 && item.stepInUiFormat.length === 0;

          return (
            <FunnelStepWrapper
              key={item.key}
              isFirstChain={index === 0}
              isLastChain={
                (showReducedUi || stepSeriesList.length >= MAX_NB_OF_STEPS) && isLastStepSeries
              }
              index={index}
              isDeletable={stepSeriesList.length > MIN_NB_OF_STEPS}
              stepSeries={item}
              dateRange={dateRange}
              isInvalid={stepsWithError.includes(item.key)}
              onStepChange={(newStepSeriesList, newDateRange) =>
                updateOne(item.key, newStepSeriesList, newDateRange)
              }
              onChainDelete={() => removeOne(item.key)}
              onUpsellRequest={onUpsellRequest}
              trackClick={trackClick}
              isFiltersDropdownVisibleByDefault={isFiltersDropdownVisibleByDefault}
            />
          );
        })}
        {!showReducedUi && stepSeriesList.length < MAX_NB_OF_STEPS && (
          <AddStepSection
            addOneChain={addOne}
            trackClick={trackClick}
            kind="step"
            index={stepSeriesList.length}
            isFunnelStep
            contextOfUse={contextOfUse}
          />
        )}
      </Steps>
    </>
  );
};
